import { Box, type Theme, Typography, useTheme } from "@mui/material";
import { CHART_PADDING_TOP_COEFICIENT } from "Charts/Utils/CommonChartUtils";
import { ChartState, type SavingsChartData, generateSavingsChartData, getSavingsChartAnnotations } from "Charts/Utils/SavingsChartUtils";
import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { getYearsInflected } from "Infrastructure/Utils/LanguageUtils";
import { Chart } from "chart.js";
import AnnotationPlugin from "chartjs-plugin-annotation";
import { addMonths, differenceInMonths, format, monthsToYears } from "date-fns";
import type React from "react";
import { Line } from "react-chartjs-2";

type Props = {
  readonly targetDate: Date;
  readonly referenceStartDate: Date;
  readonly targetAmount: number;
  readonly monthlyContributionAmount: number;
  readonly inflationRate: number;
  readonly interestRate: number;
};

Chart.register(AnnotationPlugin);

const getLabels = (lengthInMonths: number, referenceStartDate: Date) => {
  const padding = Math.floor(lengthInMonths * 0.05);
  const today = referenceStartDate;

  return new Array(lengthInMonths + padding).fill("").map((_, index) => {
    return format(addMonths(today, index), "yyyy-MM-dd");
  });
};

const getDatasets = (theme: Theme, data: SavingsChartData) => {
  const topAmountArray = data.preTargetData
    ?.concat(data?.postTargetData || [])
    .sort((a, b) => b - a)
    .slice(0, 1);
  const topAmount = topAmountArray?.[0] || 0;
  const paddedTop = topAmount * CHART_PADDING_TOP_COEFICIENT;
  const datasets = [
    {
      data: [paddedTop],
      fill: true,
      borderColor: "transparent",
      backgroundColor: "transparent",
      pointStyle: "dash",
      order: 2,
    },
  ];

  if (data.preTargetData) {
    datasets.push({
      data: data.preTargetData,
      fill: true,
      borderColor: "transparent",
      pointStyle: "dash",
      backgroundColor: theme.colors.chartNeutral,
      order: 1,
    });
  }

  if (data.postTargetData) {
    datasets.push({
      data: data.postTargetData,
      fill: true,
      borderColor: "transparent",
      pointStyle: "dash",
      order: 2,
      backgroundColor: data.chartState === ChartState.Exceeded ? theme.colors.chartPositiveLight : theme.colors.chartNeutralLighter,
    });
  }

  return datasets;
};

export const SavingsChart: React.FC<Props> = ({
  inflationRate,
  interestRate,
  monthlyContributionAmount,
  targetDate,
  referenceStartDate,
  targetAmount,
}) => {
  const theme = useTheme();
  const { t } = useResource();

  const durationInMonths = differenceInMonths(targetDate, referenceStartDate) + 1;
  const data = generateSavingsChartData({
    durationInMonths,
    targetAmount,
    interestRate,
    monthlyContributionAmount,
    inflationRate,
  });

  const options = {
    locale: "en-US",
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
    plugins: {
      title: {
        display: false,
      },
      legend: {
        display: false,
      },
      annotation: {
        annotations: {
          ...getSavingsChartAnnotations({ theme, targetAmount, data }),
        },
      },
      tooltip: {
        enabled: false,
        external: (context: any) => {
          let tooltipEl = document.getElementById("chartjs-tooltip");

          if (!tooltipEl) {
            tooltipEl = document.createElement("div");
            tooltipEl.id = "chartjs-tooltip";
            tooltipEl.innerHTML = "<table></table>";
            document.body.appendChild(tooltipEl);
          }

          const tooltipModel = context.tooltip;
          if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = "0";
            return;
          }

          tooltipEl.classList.remove("above", "below", "no-transform");
          if (tooltipModel.yAlign) {
            tooltipEl.classList.add(tooltipModel.yAlign);
          } else {
            tooltipEl.classList.add("no-transform");
          }

          function getBody(bodyItem: any) {
            return bodyItem.lines;
          }

          if (tooltipModel.body) {
            const bodyLines = tooltipModel.body.map(getBody);
            let innerHtml = "<thead>";

            const years = monthsToYears(data.actualDurationInMonths);

            // titleLines.forEach((title: any) => {
            //   innerHtml += "<tr><th>" + title + "</th></tr>";
            // });
            innerHtml += `</thead><tbody><tr><td style='font-weight: 500;'>
              ${t(Resources.AnalysisDetail.Goals.InvestmentDuration)}: ${years} ${t(getYearsInflected(years))}
              </td></tr>`;

            // const colors = tooltipModel.labelColors[i];
            // let style = "background:" + colors.backgroundColor;
            const date = format(new Date(tooltipModel.dataPoints[0].label), "M/yyyy");
            const amount = `<span>${formatCurrency(Number(bodyLines[0][0].toString().replaceAll(",", "")), CurrencyCode.CZK, 0)}</span>`;

            innerHtml += `<tr><td>${date}</td></tr><tr><td>${amount}</td></tr>`;
            innerHtml += "</tbody>";

            const tableRoot = tooltipEl.querySelector("table");

            if (tableRoot) {
              tableRoot.innerHTML = innerHtml;
            }
          }

          const chartPosition = context.chart.canvas.getBoundingClientRect();
          const tooltipPosition = tooltipEl.getBoundingClientRect();

          const width = tooltipPosition.right - tooltipPosition.left;
          const height = tooltipPosition.bottom - tooltipPosition.top;

          tooltipEl.style.opacity = "1";
          tooltipEl.style.position = "absolute";
          tooltipEl.style.left = `${chartPosition.left + window.pageXOffset + tooltipModel.caretX - width}px`;
          tooltipEl.style.top = `${chartPosition.top + window.pageYOffset + tooltipModel.caretY - height}px`;
          tooltipEl.style.padding = `${tooltipModel.padding}px`;
          tooltipEl.style.pointerEvents = "none";
          tooltipEl.style.zIndex = "10000";

          tooltipEl.style.fontFamily = "Roboto";
          tooltipEl.style.fontSize = "10px";
          tooltipEl.style.lineHeight = "1.1";
          tooltipEl.style.borderWidth = "1px";
          tooltipEl.style.borderStyle = "solid";
          tooltipEl.style.borderColor = theme.colors.borderLight;
          tooltipEl.style.backgroundColor = theme.colors.white;
          tooltipEl.style.borderRadius = theme.spacing(Radius.M);
          tooltipEl.style.padding = theme.spacing(Spacing.M);
        },
      },
    },
    scales: {
      x: {
        ticks: {
          autoSkip: true,
          font: {
            color: "black",
            weight: 600,
            size: 10,
            family: "Roboto",
            lineHeight: 1.3,
          },
          callback: (value: string | number, index: number) => {
            const date = referenceStartDate;
            const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);

            const addedMonths = addMonths(firstDayOfMonth, Number(value));

            // first milestone
            if (data.preTargetData && data.preTargetData.length - 1 === index) {
              // if ratio too small
              if (data.milestoneDifferenceRatio < 0.1 && data.milestoneDifferenceRatio !== 0) {
                return "";
              }

              if (data.chartState !== ChartState.OnTrack) {
                return format(addedMonths, "d.M.yyyy");
              }

              return format(targetDate, "d.M.yyyy");
            }

            if (data.postTargetData && data.postTargetData.length - 1 === index) {
              if (data.chartState === ChartState.Exceeded) {
                return format(targetDate, "d.M.yyyy");
              }

              return format(addedMonths, "d.M.yyyy");
            }

            // first date
            if (index === 0) {
              return format(addMonths(firstDayOfMonth, Number(value)), "yyyy");
            }

            if (index > data.actualDurationInMonths) {
              return "";
            }
          },
        },
        grid: {
          display: false,
        },
      },
      y: {
        suggestedMin: 0,
        beginAtZero: true,
        ticks: {
          display: false,
        },
        grid: {
          display: false,
        },
      },
    },
    layout: {
      padding: { right: 32 },
    },
  };

  return (
    <Box>
      <Typography variant="caption" fontSize={10} fontWeight={400} lineHeight={1.3} color="secondary">
        {t(Resources.Common.Value)}
      </Typography>
      <Box display="flex" alignItems="end">
        <Box position="relative" width={"100%"}>
          <Line
            options={options}
            data={{
              labels: getLabels(data.actualDurationInMonths, referenceStartDate),
              datasets: getDatasets(theme, data),
            }}
          />
        </Box>
        <Typography variant="caption" fontSize={10} fontWeight={400} lineHeight={1.3} color="secondary" mb={5.2} ml={-5}>
          {t(Resources.Common.Time)}
        </Typography>
      </Box>
    </Box>
  );
};
